import { Box, Text, VStack } from '@chakra-ui/react';
import { IBasket } from '../../../lib/graphql';
import BasketItemCard from './BasketItemCard';

interface BasketItemsListProps {
  basket: IBasket;
  onRemoveItem: (itemId: number, itemType: string) => void;
  variant?: 'mobile' | 'desktop';
}

/**
 * Reusable basket items list component
 * Displays all basket items with proper layout for mobile/desktop
 * Handles empty state and item removal
 */
const BasketItemsList = ({
  basket,
  onRemoveItem,
  variant = 'mobile',
}: BasketItemsListProps): JSX.Element => {
  const isDesktop = variant === 'desktop';

  if (!basket?.items?.length) {
    return (
      <Box padding={6} textAlign="center">
        <Text fontSize={isDesktop ? 'lg' : 'md'} color="gray.500">
          Your basket is empty
        </Text>
        {isDesktop && (
          <Text fontSize="sm" color="gray.400" mt={2}>
            Add some items to get started
          </Text>
        )}
      </Box>
    );
  }

  if (isDesktop) {
    return (
      <Box
        bg="white"
        borderRadius="lg"
        overflow="hidden"
        shadow="sm"
        border="1px solid"
        borderColor="gray.200"
        maxHeight="60vh"
        display="flex"
        flexDirection="column"
      >
        <Box
          bg="gray.50"
          px={6}
          py={4}
          borderBottom="1px solid"
          borderColor="gray.200"
          flexShrink={0}
        >
          <Text fontSize="lg" fontWeight="semibold" color="gray.700">
            Your Items
          </Text>
        </Box>
        <VStack spacing={0} align="stretch" overflow="auto" flex={1}>
          {basket.items.map((lineItem, index) => (
            <BasketItemCard
              key={`basketItem-${
                lineItem.id || lineItem.course?.id || lineItem.exam?.id
                || lineItem.courseSession?.id || lineItem.eventTier?.id || index
              }`}
              lineItem={lineItem}
              index={index}
              onRemoveItem={onRemoveItem}
              variant="desktop"
            />
          ))}
        </VStack>
      </Box>
    );
  }

  // Mobile layout
  return (
    <VStack spacing={3} align="stretch">
      {basket.items.map((lineItem, index) => (
        <BasketItemCard
          key={`basketItem-${
            lineItem.id || lineItem.course?.id || lineItem.exam?.id
            || lineItem.courseSession?.id || lineItem.eventTier?.id || index
          }`}
          lineItem={lineItem}
          index={index}
          onRemoveItem={onRemoveItem}
          variant="mobile"
        />
      ))}
    </VStack>
  );
};

export default BasketItemsList;
